import { View360 } from '@/components/blocks/ProductDetails/360View';
import { ProductDetailsArQrCode } from '@/components/blocks/ProductDetails/ArQrCode';
import { ProductDetailsGallery } from '@/components/blocks/ProductDetails/Gallery';
import { ProductShareLinks } from '@/components/blocks/ProductDetails/ProductShareLinks';
import { ProductTabPanel } from '@/components/blocks/ProductDetails/ProductTabPanel';
import { productGalleryTabsSX } from '@/components/blocks/ProductDetails/styles/productGalleryTabs';
import { VideoPlayerGallery } from '@/components/blocks/ProductDetails/VideoGallery';
import { ProductGalleryNavigation } from '@/components/blocks/ProductDetails/ProductGalleryNavigation';
import { ProductDetailsCarouselRef } from '@/components/blocks/ProductDetails/Carousel';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Attachment } from '@/data/types/Product';
import { Box, SxProps, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles/createTheme';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS, MEDIA } from 'shared/custom/constants/commonConstants';
import { useVideoGallery } from '@/components/blocks/ProductDetails/context/VideoGalleryContext';
import DynamicIcon from '@/components/icons/DynamicIcon';
interface ProductGalleryTabsProps {
	sku: string;
	isArEnabled?: boolean;
	arQRImageBase64?: string;
	view360URL?: string;
	isVisualSupportEnabled?: boolean;
}

type TabKeyType =
	| 'product-gallery-tabs-gallery'
	| 'cora-ar-3d-viewer'
	| 'product-gallery-tabs-ar-qr-code'
	| 'product-gallery-tabs-360'
	| 'product-gallery-tabs-video'
	| 'product-gallery-tabs-share';

type TabListType = {
	icon: JSX.Element;
	label: string;
	sx: SxProps<Theme>;
	key: TabKeyType;
	id: string;
};

const TAB_GALLERY = 'product-gallery-tabs-gallery';
const TAB_CORA_VIEWER = 'cora-ar-3d-viewer';
const TAB_360 = 'product-gallery-tabs-360';
const TAB_VIDEO = 'product-gallery-tabs-video';
const TAB_SHARE = 'product-gallery-tabs-share';

const a11yProps = (index: number, name: string) => ({
	id: name,
	'aria-controls': `tabpanel-${index}`,
	'data-position': `${index}`,
});

const useTabList = ({
	isArEnabled,
	isVisualSupportEnabled,
	isVideoAvailable,
	value,
	hoveredTab,
}: {
	isArEnabled: boolean;
	isVisualSupportEnabled?: boolean;
	isVideoAvailable: boolean;
	value: string;
	hoveredTab: TabKeyType | null;
}) => {
	const { Gallery, RealityPlus, View360, Video, Share } = useLocalization('ProductGalleryTabs');

	const [isMobileDevice, setIsMobileDevice] = useState(false);

	useEffect(() => {
		// Regex taken from 'cora-bundle.min.js'.
		// On devices matching these user agents, the QR code is hidden and users are redirected
		// to a new tab with the 3D viewer or AR experience, as in the previous react store
		// implementation.
		setIsMobileDevice(
			typeof window !== 'undefined'
				? /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent)
				: false
		);
	}, []);

	const isFilled = useCallback(
		(...keys: TabKeyType[]) =>
			keys.includes(value as TabKeyType) || keys.includes(hoveredTab as TabKeyType),
		[value, hoveredTab]
	);

	const PhotoLibrary = useCallback(
		() => (
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.galleryThumbnail}
				variant={isFilled(TAB_GALLERY) ? 'filled' : 'rounded'}
			/>
		),
		[isFilled]
	);

	const ViewInAr = useCallback(
		() => (
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.viewInAr}
				variant={
					isFilled(TAB_CORA_VIEWER, 'product-gallery-tabs-ar-qr-code') ? 'filled' : 'rounded'
				}
			/>
		),
		[isFilled]
	);

	const RotateRight = useCallback(
		() => (
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.rotate360}
				variant={isFilled(TAB_360) ? 'filled' : 'rounded'}
			/>
		),
		[isFilled]
	);

	const PlayArrow = useCallback(
		() => (
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.smartDisplay}
				variant={isFilled(TAB_VIDEO) ? 'filled' : 'rounded'}
			/>
		),
		[isFilled]
	);

	const ShareI = useCallback(
		() => (
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.share}
				variant={isFilled(TAB_SHARE) ? 'filled' : 'rounded'}
			/>
		),
		[isFilled]
	);

	return useMemo(() => {
		const isArQrCodeVisible = !isMobileDevice && isArEnabled && isVisualSupportEnabled;
		const is360ViewVisible = isArEnabled && isVisualSupportEnabled;
		const isArRedirectVisible = isMobileDevice && isArEnabled && isVisualSupportEnabled;

		return [
			{
				icon: <PhotoLibrary />,
				label: Gallery.t(),
				sx: productGalleryTabsSX.galleryTab,
				key: TAB_GALLERY,
				id: 'mediaTriggerImage',
			},
			...(isArQrCodeVisible
				? [
						{
							icon: <ViewInAr />,
							label: RealityPlus.t(),
							sx: productGalleryTabsSX.defaultTab,
							key: 'product-gallery-tabs-ar-qr-code',
							id: 'mediaTriggerAR',
						} satisfies TabListType,
				  ]
				: []),
			...(isArRedirectVisible
				? [
						{
							icon: <ViewInAr />,
							label: RealityPlus.t(),
							sx: productGalleryTabsSX.defaultTab,
							key: TAB_CORA_VIEWER,
							id: 'mediaTriggerAR',
						} satisfies TabListType,
				  ]
				: []),
			...(is360ViewVisible
				? [
						{
							icon: <RotateRight />,
							label: View360.t(),
							sx: productGalleryTabsSX.defaultTab,
							key: TAB_360,
							id: 'mediaTrigger360',
						} satisfies TabListType,
				  ]
				: []),
			...(isVideoAvailable
				? [
						{
							icon: <PlayArrow />,
							label: Video.t(),
							sx: productGalleryTabsSX.defaultTab,
							key: TAB_VIDEO,
							id: 'mediaTriggerVideo',
						} satisfies TabListType,
				  ]
				: []),
			{
				icon: <ShareI />,
				label: Share.t(),
				sx: productGalleryTabsSX.defaultTab,
				key: TAB_SHARE,
				id: 'shareTrigger',
			},
		] satisfies TabListType[];
	}, [
		isMobileDevice,
		isArEnabled,
		isVisualSupportEnabled,
		PhotoLibrary,
		Gallery,
		ViewInAr,
		RealityPlus,
		RotateRight,
		View360,
		isVideoAvailable,
		PlayArrow,
		Video,
		ShareI,
		Share,
	]);
};

export const ProductGalleryTabs = ({
	isArEnabled = false,
	arQRImageBase64 = EMPTY_STRING,
	view360URL = EMPTY_STRING,
	isVisualSupportEnabled = false,
}: ProductGalleryTabsProps) => {
	const theme = useTheme();
	const isMobileXs = useMediaQuery(theme.breakpoints.down('sm'));
	const [value, setValue] = useState<TabKeyType>(TAB_GALLERY);
	const [hoveredTab, setHoveredTab] = useState<TabKeyType | null>(null);
	// The 'cora-ar-3d-viewer' tab triggers an external AR experience and does not render a panel.
	// To prevent switching tabs when it's clicked, handleChange ignores this tab's selection.
	const handleChange = (_event: React.SyntheticEvent, newValue: TabKeyType) =>
		setValue((prevValue) => (newValue === TAB_CORA_VIEWER ? prevValue : newValue));

	const { selection } = useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useSkuListTable>;

	const attachments = selection?.sku?.attachments;
	const mediaAttachments = attachments?.filter(
		(attachment) => attachment.usage === MEDIA && attachment.mimeType === 'content/url'
	);

	const totalVideoSlides = mediaAttachments?.length;

	const tabList = useTabList({
		isArEnabled,
		isVisualSupportEnabled,
		isVideoAvailable: !!mediaAttachments?.length,
		value,
		hoveredTab,
	});

	const galleryCarouselRef = useRef<ProductDetailsCarouselRef>(null);
	const [totalGallerySlides, setTotalGallerySlides] = useState(0);
	const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
	const { actualVideo, setActualVideo, swiperRefVideoGallery, setIsVideoPlayerActive } =
		useVideoGallery();

	const currentVideoSlide =
		mediaAttachments?.findIndex(
			(video) => video.attachmentAssetPath === actualVideo?.attachmentAssetPath
		) ?? -1;

	const handleChangeVideo = useCallback(
		(videoData: Attachment) => {
			setActualVideo(videoData);
			setIsVideoPlayerActive(true);
		},
		[setActualVideo, setIsVideoPlayerActive]
	);

	const handlePrevVideo = () => {
		if (currentVideoSlide === -1 || !mediaAttachments) return;
		if (currentVideoSlide > 0) {
			handleChangeVideo(mediaAttachments[currentVideoSlide - 1]);
			swiperRefVideoGallery.current?.slideTo(currentVideoSlide - 1);
		}
	};

	const handleNextVideo = () => {
		if (currentVideoSlide === -1 || !mediaAttachments) return;
		if (currentVideoSlide < mediaAttachments.length - 1) {
			handleChangeVideo(mediaAttachments[currentVideoSlide + 1]);
			swiperRefVideoGallery.current?.slideTo(currentVideoSlide + 1);
		}
	};

	return (
		<>
			<Box sx={productGalleryTabsSX.tabsContainer}>
				<Tabs
					value={value}
					onChange={handleChange}
					aria-label="product view tabs with icons"
					TabIndicatorProps={{ style: { display: 'none' } }}
					variant="scrollable"
					scrollButtons={false}
					sx={productGalleryTabsSX.tabs}
				>
					{tabList.map((tab, index) =>
						tab.key === TAB_CORA_VIEWER ? (
							<Tab
								{...a11yProps(index, tab.id)}
								key={tab.key}
								value={tab.key}
								icon={tab.icon}
								label={
									<Typography variant={isMobileXs ? 'bodyCaption' : 'bodySubText'}>
										{tab.label}
									</Typography>
								}
								sx={tab.sx}
								onClick={() => window.open(view360URL + '&autoAR=true', '_blank')}
								onMouseEnter={() => setHoveredTab(tab.key)}
								onMouseLeave={() => setHoveredTab(null)}
								disableRipple
							/>
						) : (
							<Tab
								{...a11yProps(index, tab.id)}
								key={tab.key}
								value={tab.key}
								icon={tab.icon}
								label={
									<Typography variant={isMobileXs ? 'bodyCaption' : 'bodySubText'}>
										{tab.label}
									</Typography>
								}
								sx={tab.sx}
								onMouseEnter={() => setHoveredTab(tab.key)}
								onMouseLeave={() => setHoveredTab(null)}
								disableRipple
							/>
						)
					)}
				</Tabs>

				{value === TAB_GALLERY ? (
					// eslint-disable-next-line react/forbid-component-props
					<ProductGalleryNavigation
						totalSlides={totalGallerySlides}
						currentSlide={currentGallerySlide}
						onPrev={() => galleryCarouselRef.current?.goToPrev()}
						onNext={() => galleryCarouselRef.current?.goToNext()}
						id="mediaImageNavControl"
						className="media-image__navigation-control"
						prevClassName="media-image__navigation-control--prev"
						nextClassName="media-image__navigation-control--next"
					/>
				) : value === TAB_VIDEO ? (
					// eslint-disable-next-line react/forbid-component-props
					<ProductGalleryNavigation
						totalSlides={totalVideoSlides ?? 0}
						currentSlide={currentVideoSlide ?? 0}
						onPrev={handlePrevVideo}
						onNext={handleNextVideo}
						id="mediaVideoNavControl"
						className="media-video__navigation-control"
						prevClassName="media-video__navigation-control--prev"
						nextClassName="media-video__navigation-control--next"
					/>
				) : null}
			</Box>

			{tabList.map((tab) => (
				<ProductTabPanel key={tab.key} value={tab.key} hidden={value !== tab.key}>
					<PanelContent
						tabKey={tab.key}
						arQRImageBase64={arQRImageBase64}
						view360URL={view360URL}
						videos={mediaAttachments}
						galleryCarouselRef={galleryCarouselRef}
						onTotalSlidesChange={setTotalGallerySlides}
						onCurrentSlideChange={setCurrentGallerySlide}
						handleChangeVideo={handleChangeVideo}
						handlePrevVideo={handlePrevVideo}
						handleNextVideo={handleNextVideo}
					/>
				</ProductTabPanel>
			))}
		</>
	);
};

const PanelContent = memo(
	({
		tabKey,
		arQRImageBase64,
		view360URL,
		videos,
		galleryCarouselRef,
		onTotalSlidesChange,
		onCurrentSlideChange,
		handleChangeVideo,
		handlePrevVideo,
		handleNextVideo,
	}: {
		tabKey: TabKeyType;
		arQRImageBase64: string;
		view360URL: string;
		videos: Attachment[] | undefined;
		galleryCarouselRef: React.RefObject<ProductDetailsCarouselRef>;
		onTotalSlidesChange: (totalSlides: number) => void;
		onCurrentSlideChange: (currentSlide: number) => void;
		handleChangeVideo: (videoData: Attachment) => void;
		handlePrevVideo: () => void;
		handleNextVideo: () => void;
	}) =>
		({
			'product-gallery-tabs-gallery': (
				<ProductDetailsGallery
					carouselRef={galleryCarouselRef}
					onTotalSlidesChange={onTotalSlidesChange}
					onCurrentSlideChange={onCurrentSlideChange}
				/>
			),
			'product-gallery-tabs-ar-qr-code': (
				<ProductDetailsArQrCode arQRImageBase64={arQRImageBase64} />
			),
			'cora-ar-3d-viewer': null,
			'product-gallery-tabs-360': <View360 view360URL={view360URL} />,
			'product-gallery-tabs-video': (
				<VideoPlayerGallery
					videos={videos}
					handleChangeVideo={handleChangeVideo}
					handlePrevVideo={handlePrevVideo}
					handleNextVideo={handleNextVideo}
				/>
			),
			'product-gallery-tabs-share': <ProductShareLinks />,
		}[tabKey])
);
